import React, { useState, useEffect, useCallback, useRef, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Play, Pause, SkipForward, SpeakerHigh, SpeakerSlash,
  Broadcast, MusicNote, Microphone, Gear, UserCircle,
  PencilSimple, X, Check, Plus, Trash, ArrowLeft,
  Fire, Clock, Waveform, WifiSlash, Radio, Upload,
  ArrowsClockwise, Globe, Heart, ChatCircle, ShareNetwork, CopySimple
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const Game = lazy(() => import('./pages/Game'));

// ============ Starfield ============
function Starfield() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const stars = Array.from({ length: 120 }, () => ({ x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight, r: Math.random() * 1.2 + 0.3, a: Math.random(), s: Math.random() * 0.005 + 0.002 }));
    let raf;
    const draw = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; ctx.clearRect(0, 0, canvas.width, canvas.height); stars.forEach(s => { s.a += s.s; ctx.fillStyle = `rgba(57,255,20,${(0.3 + Math.abs(Math.sin(s.a)) * 0.7) * 0.4})`; ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2); ctx.fill(); }); raf = requestAnimationFrame(draw); };
    draw(); return () => cancelAnimationFrame(raf);
  }, []);
  return <canvas ref={canvasRef} className="starfield-canvas" />;
}

// ============ Station Context ============
const StationContext = createContext(null);

function StationProvider({ children }) {
  const [stationState, setStationState] = useState(null);
  const [tunedIn, setTunedIn] = useState(false);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [ytReady, setYtReady] = useState(false);
  const [howardAudio, setHowardAudio] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBumper, setIsBumper] = useState(false);
  const [listeners, setListeners] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const playerRef = useRef(null);
  const timerRef = useRef(null);
  const howardAudioRef = useRef(null);
  const lastVideoIdRef = useRef(null);
  const currentSegmentRef = useRef(null);
  const volumeRef = useRef(80);
  const isMutedRef = useRef(false);
  const heartbeatRef = useRef(null);
  const listenerIdRef = useRef(null);
  const stationStateRef = useRef(null);

  useEffect(() => { volumeRef.current = volume; }, [volume]);
  useEffect(() => { isMutedRef.current = isMuted; }, [isMuted]);
  useEffect(() => { stationStateRef.current = stationState; }, [stationState]);

  const fetchStationState = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/station/state`);
      setStationState(r.data);
      if (r.data.listeners !== undefined) setListeners(r.data.listeners);
      setLoading(false);
    } catch { setLoading(false); }
  }, []);

  // Load YT IFrame API
  useEffect(() => {
    if (window.YT && window.YT.Player) { setYtReady(true); return; }
    const tag = document.createElement('script'); tag.src = 'https://www.youtube.com/iframe_api';
    document.getElementsByTagName('script')[0].parentNode.insertBefore(tag, document.getElementsByTagName('script')[0]);
    window.onYouTubeIframeAPIReady = () => setYtReady(true);
  }, []);

  // ENDED handler — when video ends, immediately play next content
  const videoEndedRef = useRef(false);
  const onEndedRef = useRef(null);
  useEffect(() => {
    onEndedRef.current = () => {
      videoEndedRef.current = true;
      setTransitioning(true);
      // If we know the next track, play it immediately without waiting for server
      if (stationStateRef.current?.next_track?.youtube_video_id && playerRef.current) {
        const next = stationStateRef.current.next_track;
        try {
          playerRef.current.loadVideoById({ videoId: next.youtube_video_id, startSeconds: 0 });
          lastVideoIdRef.current = next.youtube_video_id;
        } catch {}
        currentSegmentRef.current = null;
      }
      fetchStationState();
    };
  }, [fetchStationState]);

  // Report actual video duration to backend (self-heals duration=0 tracks)
  const reportedDurationsRef = useRef(new Set());
  const reportDuration = useCallback((dur) => {
    if (!stationState?.current_track?.id || dur <= 0) return;
    const trackId = stationState.current_track.id;
    if (reportedDurationsRef.current.has(trackId)) return;
    reportedDurationsRef.current.add(trackId);
    axios.patch(`${API}/tracks/${trackId}/duration`, { duration: Math.round(dur) }).catch(() => {});
  }, [stationState?.current_track?.id]);

  // Play any YouTube video in the single player
  const playVideo = useCallback((videoId, startAt = 0) => {
    if (!playerRef.current) {
      const el = document.getElementById('yt-player');
      if (!el) return;
      playerRef.current = new window.YT.Player('yt-player', {
        videoId, playerVars: { autoplay: 1, controls: 0, modestbranding: 1, rel: 0, showinfo: 0, iv_load_policy: 3, playsinline: 1, start: startAt },
        events: {
          onReady: (e) => {
            e.target.setVolume(isMutedRef.current ? 0 : volumeRef.current);
            const d = e.target.getDuration() || 0;
            setDuration(d);
            lastVideoIdRef.current = videoId;
            if (d > 0 && reportDurationRef.current) reportDurationRef.current(d);
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.PLAYING) {
              setTransitioning(false);
              const d = e.target.getDuration() || 0;
              setDuration(d);
              if (d > 0 && reportDurationRef.current) reportDurationRef.current(d);
            }
            else if (e.data === window.YT.PlayerState.ENDED && onEndedRef.current) onEndedRef.current();
          }
        }
      });
    } else if (lastVideoIdRef.current !== videoId) {
      try { playerRef.current.loadVideoById({ videoId, startSeconds: startAt }); lastVideoIdRef.current = videoId; } catch {}
    } else {
      try { playerRef.current.playVideo(); } catch {}
    }
  }, []);

  const reportDurationRef = useRef(null);
  useEffect(() => { reportDurationRef.current = reportDuration; }, [reportDuration]);

  // Main segment controller
  useEffect(() => {
    if (!ytReady || !tunedIn || !stationState) return;
    const segType = stationState.segment_type;
    const segKey = `${segType}-${stationState.timeline_index}`;
    if (currentSegmentRef.current === segKey) {
      // Same segment — but if the video already ended, don't try to replay it
      // Just wait for the station to advance to the next segment
      return;
    }
    currentSegmentRef.current = segKey;
    videoEndedRef.current = false;

    // Clean up howard audio
    if (howardAudioRef.current) { try { howardAudioRef.current.pause(); } catch {} howardAudioRef.current = null; setHowardAudio(null); }

    if (segType === 'track' && stationState.current_track?.youtube_video_id) {
      setIsBumper(false);
      const startPos = Math.max(0, Math.floor(stationState.position_in_segment || 0));
      playVideo(stationState.current_track.youtube_video_id, startPos);
    } else if (segType === 'bumper' && stationState.bumper_video_id) {
      setIsBumper(true);
      playVideo(stationState.bumper_video_id, 0);
    } else if (segType === 'howard') {
      setIsBumper(true);
      if (stationState.howard_video_id) {
        playVideo(stationState.howard_video_id, 0);
      } else if (stationState.howard_audio_url) {
        if (playerRef.current) try { playerRef.current.pauseVideo(); } catch {}
        const audio = gestureAudioRef.current || new Audio();
        audio.src = `${BACKEND_URL}${stationState.howard_audio_url}`;
        audio.volume = (isMutedRef.current ? 0 : volumeRef.current) / 100;
        howardAudioRef.current = audio;
        setHowardAudio(audio);
        const tryPlay = () => { audio.play().catch((e) => console.warn('Howard audio:', e)); };
        if (audio.readyState >= 2) tryPlay(); else { audio.oncanplay = () => { tryPlay(); audio.oncanplay = null; }; audio.load(); }
        audio.onended = () => {
          setHowardAudio(null); howardAudioRef.current = null;
          currentSegmentRef.current = null;
          fetchStationState();
        };
      }
    } else if (segType === 'howard_bumper') {
      setIsBumper(true);
      if (playerRef.current) try { playerRef.current.pauseVideo(); } catch {}
      const audio = gestureAudioRef.current || new Audio();
      audio.src = `${BACKEND_URL}${stationState.howard_bumper_url || '/api/howard/bumper'}`;
      audio.volume = (isMutedRef.current ? 0 : volumeRef.current) / 100;
      howardAudioRef.current = audio;
      setHowardAudio(audio);
      const tryPlay = () => { audio.play().catch((e) => console.warn('Bumper audio:', e)); };
      if (audio.readyState >= 2) tryPlay(); else { audio.oncanplay = () => { tryPlay(); audio.oncanplay = null; }; audio.load(); }
      audio.onended = () => {
        setHowardAudio(null); howardAudioRef.current = null;
        currentSegmentRef.current = null;
        fetchStationState();
      };
    }
  }, [ytReady, tunedIn, stationState?.segment_type, stationState?.timeline_index, playVideo, fetchStationState]);

  // Volume sync
  useEffect(() => {
    if (playerRef.current) try { playerRef.current.setVolume(isMuted ? 0 : volume); } catch {}
    if (howardAudioRef.current) howardAudioRef.current.volume = (isMuted ? 0 : volume) / 100;
  }, [volume, isMuted]);

  // Time tracker
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (tunedIn) { timerRef.current = setInterval(() => { if (playerRef.current) try { const t = playerRef.current.getCurrentTime(); if (t) setCurrentTime(t); } catch {} }, 1000); }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [tunedIn]);

  // Pre-create audio element during user gesture (TUNE IN) for mobile compatibility
  const gestureAudioRef = useRef(null);

  // Listener ID for heartbeat
  useEffect(() => {
    let id = localStorage.getItem('sp_listener_id');
    if (!id) { id = 'sp-' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36); localStorage.setItem('sp_listener_id', id); }
    listenerIdRef.current = id;
  }, []);

  // Heartbeat: send every 30s while tuned in
  useEffect(() => {
    if (tunedIn && listenerIdRef.current) {
      const sendHeartbeat = () => {
        axios.post(`${API}/station/heartbeat`, { listener_id: listenerIdRef.current }).then(r => {
          if (r.data.listeners !== undefined) setListeners(r.data.listeners);
        }).catch(() => {});
      };
      sendHeartbeat();
      heartbeatRef.current = setInterval(sendHeartbeat, 30000);
      return () => { clearInterval(heartbeatRef.current); };
    } else {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    }
  }, [tunedIn]);

  const tuneIn = () => {
    setTunedIn(true); setIsMuted(false);
    // Create and "unlock" an Audio element during user gesture — required for mobile
    if (!gestureAudioRef.current) {
      const audio = new Audio();
      audio.src = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYoRwmHAAAAAAD/+1DEAAAH+ANp9AAACQM07T80AAATAAAT/AAAExMTEwAABMTE1MAAAAAAAAAAAAATAQEBAQA'; // tiny silent mp3
      audio.volume = 0.01;
      audio.play().then(() => { audio.pause(); audio.currentTime = 0; }).catch(() => {});
      gestureAudioRef.current = audio;
    }
  };
  const toggleMute = () => setIsMuted(!isMuted);
  const adminSkip = () => { (async () => { try { await axios.post(`${API}/station/advance`); currentSegmentRef.current = null; fetchStationState(); } catch {} })(); };
  const hasContent = stationState?.current_track || stationState?.next_track || (stationState?.fresh_tracks?.length > 0);

  useEffect(() => { fetchStationState(); }, [fetchStationState]);
  useEffect(() => { const i = setInterval(fetchStationState, 5000); return () => clearInterval(i); }, [fetchStationState]);

  return (
    <StationContext.Provider value={{ stationState, tunedIn, tuneIn, volume, setVolume, isMuted, toggleMute, howardAudio, currentTime, duration, loading, fetchStationState, isAdmin, setIsAdmin, adminSkip, isBumper, hasContent, listeners, transitioning }}>
      {children}
    </StationContext.Provider>
  );
}
function useStation() { return useContext(StationContext); }

// ============ Player Bar ============
function PlayerBar() {
  const { stationState, tunedIn, tuneIn, volume, setVolume, isMuted, toggleMute, currentTime, duration } = useStation();
  const track = stationState?.current_track;
  const isHoward = stationState?.segment_type === 'howard';
  const location = useLocation();
  const fmt = (s) => { if (!s || isNaN(s)) return "0:00"; return `${Math.floor(s/60)}:${Math.floor(s%60).toString().padStart(2,'0')}`; };
  return (
    <div className="sp-player-bar" data-testid="player-bar">
      {track?.thumbnail && <img src={track.thumbnail} alt="" className="sp-player-thumb" />}
      <div className="sp-player-track">
        {isHoward ? (<><div className="sp-player-title howard-active">HOWARD BURNS</div><div className="sp-player-artist">ON AIR...</div></>) :
         track ? (<><div className="sp-player-title">{track.title}</div><div className="sp-player-artist">{track.artist}</div></>) :
         (<div className="sp-player-title">SPACE PIFF RADIO</div>)}
      </div>
      <div className="sp-player-controls">
        {!tunedIn ? (
          <button onClick={tuneIn} className="sp-btn" style={{padding:'6px 14px',fontSize:'0.6rem'}} data-testid="player-tune-in"><Radio size={14} weight="fill" /> TUNE IN</button>
        ) : (<div style={{display:'flex',alignItems:'center',gap:'4px'}}><span className="sp-live-dot" /><span style={{fontSize:'0.55rem',fontWeight:700,letterSpacing:'0.15em'}}>LIVE</span></div>)}
      </div>
      {tunedIn && <div className="sp-player-waveform">{[...Array(25)].map((_,i) => <span key={i} className="sp-wave-bar" style={{animationDelay:`${i*0.04}s`}} />)}</div>}
      {tunedIn && <div className="sp-player-time">{fmt(currentTime)} / {fmt(duration)}</div>}
      {tunedIn && (
        <div className="sp-player-vol">
          <button onClick={toggleMute} className="sp-vol-btn" data-testid="player-mute-btn">{isMuted ? <SpeakerSlash size={16} /> : <SpeakerHigh size={16} />}</button>
          <input type="range" min="0" max="100" value={isMuted?0:volume} onChange={e=>setVolume(Number(e.target.value))} className="sp-vol-slider" data-testid="player-volume" />
        </div>
      )}
      {location.pathname !== '/' && <Link to="/" className="sp-player-nav-link" data-testid="go-to-radio"><Broadcast size={14} /> LIVE</Link>}
    </div>
  );
}

// ============ Positioning Strip ============
// Quiet orientation slate. Sets meaning before the signal hits.
// Visually subtle — this is orientation, not marketing.
function PositioningStrip() {
  return (
    <section className="sp-positioning" data-testid="positioning-strip" aria-label="orientation">
      <div className="sp-positioning-inner">
        <p className="sp-positioning-lede">
          SpacePiff is the public broadcast network of the Space Weed Universe.
        </p>
        <p className="sp-positioning-catalog" aria-hidden="true">
          <span>Music.</span>
          <span>Films.</span>
          <span>Stories.</span>
          <span>Signals.</span>
        </p>
        <p className="sp-positioning-coda">
          The first transmission is free.<br />
          <span className="sp-positioning-coda-dim">The civilization lives beyond the broadcast.</span>
        </p>
      </div>
    </section>
  );
}

// ============ Cross-link helper ============
// Always route SpacePiff -> TheMarsSpace via our own /go/marsspace.
// The component logs the click server-side, then redirects to TMS landing
// with ref=spacepiff&src={src} so TMS can attribute the funnel.
function GoMarsspace() {
  const [message, setMessage] = useState('OPENING CHANNEL...');
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const src = params.get('src') || 'unknown';
    (async () => {
      let target = `https://themarsspace.com/landing/?ref=spacepiff&src=${encodeURIComponent(src)}`;
      try {
        const r = await axios.post(`${API}/cross-link/click`, { src, referrer: document.referrer || null });
        if (r.data && r.data.target) target = r.data.target;
      } catch {}
      setMessage('TUNING TO THE MARS SPACE...');
      // small intentional delay so the in-universe message is perceptible
      setTimeout(() => { window.location.replace(target); }, 350);
    })();
  }, []);
  return (
    <div className="sp-redirect-overlay" data-testid="go-marsspace">
      <div className="sp-redirect-content">
        <div className="sp-redirect-dot" />
        <div className="sp-redirect-text">{message}</div>
        <div className="sp-redirect-sub">:: cross-network handoff in progress ::</div>
      </div>
    </div>
  );
}

// ============ Transmission List (email capture) ============
export function TransmissionList({ source = 'homepage', compact = false }) {
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState(''); // bots love filling this
  const [state, setState] = useState('idle'); // idle | submitting | success | already | error
  const [errorMsg, setErrorMsg] = useState('');
  const mountedAtRef = useRef(Date.now());

  const submit = async (e) => {
    e?.preventDefault?.();
    if (state === 'submitting') return;
    const trimmed = email.trim();
    if (!trimmed) { setState('error'); setErrorMsg('Enter an email frequency.'); return; }
    setState('submitting');
    try {
      const r = await axios.post(`${API}/list/subscribe`, {
        email: trimmed,
        source,
        honeypot,
        form_age_ms: Date.now() - mountedAtRef.current,
      });
      if (r.data?.already_subscribed) { setState('already'); }
      else { setState('success'); }
    } catch (err) {
      setState('error');
      setErrorMsg(err.response?.data?.detail === 'invalid email'
        ? 'That signal is malformed. Check your email.'
        : 'Transmission failed. Try again in a moment.');
    }
  };

  if (state === 'success' || state === 'already') {
    return (
      <div className={`sp-transmission ${compact ? 'is-compact' : ''}`} data-testid={`transmission-success-${source}`}>
        <div className="sp-transmission-success">
          <div className="sp-live-dot" style={{width:'8px',height:'8px'}} />
          <div>
            <div className="sp-transmission-title">
              {state === 'already' ? 'SIGNAL ALREADY LOCKED.' : 'SIGNAL RECEIVED.'}
            </div>
            <div className="sp-transmission-sub">
              {state === 'already'
                ? 'You\u2019re on the frequency. Stand by for the next broadcast.'
                : 'Stand by. The next transmission finds you.'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form className={`sp-transmission ${compact ? 'is-compact' : ''}`} onSubmit={submit} data-testid={`transmission-form-${source}`}>
      <div className="sp-transmission-header">
        <Broadcast size={14} weight="fill" />
        <span className="sp-transmission-label">JOIN THE TRANSMISSION LIST</span>
      </div>
      <div className="sp-transmission-tagline">
        Coded broadcasts from Space Weed Universe. No spam, just signal.
      </div>
      <div className="sp-transmission-row">
        <input
          type="email"
          className="sp-transmission-input"
          placeholder="your.frequency@signal.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid={`transmission-email-${source}`}
        />
        {/* Honeypot — visually hidden, off-screen. Bots fill, humans don't. */}
        <input
          type="text"
          name="company_url"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="sp-hp"
        />
        <button
          type="submit"
          className="sp-btn"
          disabled={state === 'submitting'}
          data-testid={`transmission-submit-${source}`}
        >
          {state === 'submitting' ? '[ TRANSMITTING... ]' : '[ RECEIVE THE SIGNAL ]'}
        </button>
      </div>
      {state === 'error' && <div className="sp-transmission-error" data-testid={`transmission-error-${source}`}>{errorMsg}</div>}
    </form>
  );
}

// ============ Beyond the Broadcast — quiet cross-network invitation ============
// SpacePiff introduces the world. Mars Space welcomes people into it.
// Never recruit. Invite discovery.
function BeyondTheBroadcastBlock({ src = 'homepage' }) {
  return (
    <section className="sp-cta-block" data-testid="beyond-the-broadcast-block">
      <div className="sp-cta-eyebrow"><Broadcast size={12} weight="fill" /> CROSS-NETWORK</div>
      <h3 className="sp-cta-headline">
        What you&rsquo;re hearing is the first transmission.<br/>
        <span className="sp-cta-headline-accent">The civilization lives beyond the broadcast.</span>
      </h3>
      <a
        href={`/go/marsspace?src=${encodeURIComponent(src)}`}
        className="sp-btn sp-btn-lg"
        data-testid="beyond-the-broadcast-cta"
      >
        [ BEYOND THE BROADCAST &rarr; ]
      </a>
    </section>
  );
}

// ============ Featured Project ============
// The full-film video ID for "Disclosure Night". Kept here intentionally
// (rather than in the DB) so it's obvious where to swap when the film URL changes.
const DISCLOSURE_NIGHT_FILM_VIDEO_ID = 'm0q5GgvTU4U';

function FeaturedProject() {
  const [project, setProject] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState('teaser'); // 'teaser' | 'film'
  const playerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/projects/featured`);
        if (r.data && r.data.youtube_video_id) setProject(r.data);
      } catch {}
    })();
  }, []);

  // Tear down the player when switching tabs so the next play loads the right video
  const stopPlayer = () => {
    if (playerRef.current) {
      try { playerRef.current.destroy(); } catch {}
      playerRef.current = null;
    }
    setPlaying(false);
  };

  const handleTabClick = (tab) => {
    if (tab === activeTab) return;
    stopPlayer();
    setActiveTab(tab);
  };

  const handlePlay = () => {
    if (!project) return;
    if (playing) {
      stopPlayer();
      return;
    }
    const videoId = activeTab === 'film'
      ? DISCLOSURE_NIGHT_FILM_VIDEO_ID
      : project.youtube_video_id;
    setPlaying(true);
    // wait for the player div to mount in this render cycle
    setTimeout(() => {
      const el = document.getElementById('featured-player');
      if (!el) return;
      playerRef.current = new window.YT.Player('featured-player', {
        videoId,
        playerVars: { autoplay: 1, modestbranding: 1, rel: 0, playsinline: 1 },
        events: {
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.ENDED) { setPlaying(false); }
          }
        }
      });
    }, 0);
  };

  if (!project) return null;

  const isFilm = activeTab === 'film';
  const ctaLabel = isFilm ? '[ WATCH THE FILM ]' : '[ WATCH TEASER ]';

  return (
    <section className="sp-hero" data-testid="featured-project">
      <div className="sp-section-label"><Fire size={16} weight="fill" /> FEATURED</div>
      <div className="sp-hero-tabs" role="tablist" data-testid="featured-tabs">
        <button
          role="tab"
          aria-selected={!isFilm}
          className={`sp-hero-tab ${!isFilm ? 'is-active' : ''}`}
          onClick={() => handleTabClick('teaser')}
          data-testid="featured-tab-teaser"
        >
          TEASER
        </button>
        <button
          role="tab"
          aria-selected={isFilm}
          className={`sp-hero-tab ${isFilm ? 'is-active' : ''}`}
          onClick={() => handleTabClick('film')}
          data-testid="featured-tab-film"
        >
          WATCH THE FILM
        </button>
      </div>
      <div className="sp-hero-panel">
        <div className="sp-hero-video" data-testid="featured-video">
          {!playing ? (
            <button className="sp-featured-play-overlay" onClick={handlePlay} data-testid="featured-play">
              <img
                src={isFilm
                  ? `https://i.ytimg.com/vi/${DISCLOSURE_NIGHT_FILM_VIDEO_ID}/sddefault.jpg`
                  : project.thumbnail}
                alt={project.title}
                className="sp-featured-thumb"
              />
              <div className="sp-featured-play-icon"><Play size={48} weight="fill" /></div>
            </button>
          ) : (
            <div id="featured-player" style={{width:'100%',height:'100%'}} />
          )}
        </div>
        <div className="sp-hero-info">
          <div className="sp-hero-tag"><Fire size={12} weight="fill" /> FEATURED PROJECT</div>
          <h2 className="sp-hero-title" data-testid="featured-title">{project.title}</h2>
          <div className="sp-hero-artist">{project.artist}</div>
          <div className="sp-hero-tags">
            {isFilm
              ? <span className="sp-tag">FULL FILM</span>
              : <span className="sp-tag">TRAILER 1</span>}
            {project.genre && <span className="sp-tag">{project.genre}</span>}
            {project.mood && <span className="sp-tag">{project.mood}</span>}
          </div>
          {project.description && <p style={{fontSize:'0.65rem',color:'var(--sp-text-dim)',lineHeight:1.5,margin:'8px 0 0'}}>{project.description}</p>}
          <div className="sp-hero-controls" style={{marginTop:'12px'}}>
            <button onClick={handlePlay} className="sp-btn" data-testid="featured-play-btn">
              {playing ? <><Pause size={14} weight="fill" /> [ STOP ]</> : <><Play size={14} weight="fill" /> {ctaLabel}</>}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============ Radio Station ============
function RadioStation() {
  const { stationState, tunedIn, tuneIn, howardAudio, loading, toggleMute, isMuted, isBumper, hasContent, listeners, transitioning } = useStation();
  const track = stationState?.current_track;
  const nextTrack = stationState?.next_track;
  const isHoward = stationState?.segment_type === 'howard';
  const showBumperVisual = (!track && hasContent && stationState?.segment_type !== 'bumper' && stationState?.segment_type !== 'howard');
  // Show YT player for tracks, bumpers, AND howard (all are YouTube videos now)
  const hideYtPlayer = (!track && hasContent && stationState?.segment_type !== 'bumper' && stationState?.segment_type !== 'howard');

  return (
    <div className="radio-page" data-testid="radio-page">
      <header className="sp-header">
        <div className="sp-logo-area">
          <img src="https://customer-assets.emergentagent.com/job_13d935ec-a0ff-420a-b535-780461974d0e/artifacts/6m5p0qml_IMG_3799.png" alt="Space Piff" className="sp-logo-img" data-testid="sp-logo" />
          <div><span className="sp-logo-text">SPACE PIFF</span><div className="sp-logo-sub">PIRATE BROADCAST NETWORK</div></div>
        </div>
        <div className="sp-header-right">
          {listeners >= 2 && (
            <div className="sp-listeners-badge" data-testid="listeners-count">
              <span className="sp-live-dot" />
              <span>{listeners} listening</span>
            </div>
          )}
          <Link to="/profile/emilio" className="sp-nav-btn" data-testid="nav-profile"><UserCircle size={14} /> EMILIO</Link>
          <Link to="/arcade" className="sp-nav-btn sp-arcade-btn" data-testid="nav-arcade">ARCADE</Link>
        </div>
      </header>

      <div className="sp-ticker" data-testid="sp-ticker">
        <div className="sp-ticker-inner">:: PIRATE BROADCAST NETWORK ONLINE :: TUNE IN TO THE SIGNAL :: BABYLON CANNOT STOP THE SIGNAL :: SPACE PIFF RADIO 24/7 :: PIRATE BROADCAST NETWORK ONLINE :: TUNE IN TO THE SIGNAL :: BABYLON CANNOT STOP THE SIGNAL :: SPACE PIFF RADIO 24/7 ::</div>
      </div>

      {/* Orientation slate. Quiet. Sets meaning before the signal hits. */}
      <PositioningStrip />

      <FeaturedProject />

      {/* Cross-network invitation: SpacePiff routes through /go/marsspace */}
      <BeyondTheBroadcastBlock src="homepage" />

      {/* Transmission list email capture — capture before bounce */}
      <section className="sp-section" data-testid="transmission-section-home">
        <TransmissionList source="homepage" />
      </section>

      {/* SPACE PIFF RADIO - Compact Player */}
      <section className="sp-radio-section" data-testid="sp-hero">
        <div className="sp-section-label"><Broadcast size={16} /> SPACE PIFF RADIO</div>
        {loading && !hasContent ? (
          <div className="sp-empty" data-testid="radio-loading"><Waveform size={56} style={{animation:'pulse-glow-text 1.5s ease-in-out infinite'}} /><h2>TUNING IN...</h2><p>:: connecting to pirate signal ::</p></div>
        ) : !hasContent ? (
          <div className="sp-empty" data-testid="radio-empty"><WifiSlash size={56} /><h2>NO SIGNAL</h2><p>:: station needs transmissions ::</p></div>
        ) : (
          <div className="sp-radio-compact">
            <div className="sp-radio-video" data-testid="video-container">
              <div id="yt-player" style={{width:'100%',height:'100%', display: hideYtPlayer ? 'none' : 'block'}} />
              {!track && hasContent && stationState?.segment_type !== 'bumper' && stationState?.segment_type !== 'howard' && tunedIn && (
                <div className="sp-bumper-visual"><img src="https://customer-assets.emergentagent.com/job_13d935ec-a0ff-420a-b535-780461974d0e/artifacts/6m5p0qml_IMG_3799.png" alt="" className="sp-bumper-logo" /><div className="sp-bumper-text">SPACE PIFF RADIO</div></div>
              )}
              {!tunedIn && (
                <button className="sp-tune-overlay" onClick={tuneIn} data-testid="tune-in-overlay"><Radio size={44} weight="fill" /><span className="sp-tune-label">[ TUNE IN ]</span><span className="sp-tune-sub">:: join the live broadcast ::</span></button>
              )}
              {tunedIn && transitioning && (
                <div className="sp-transition-overlay" data-testid="transition-overlay">
                  <img src="https://customer-assets.emergentagent.com/job_13d935ec-a0ff-420a-b535-780461974d0e/artifacts/6m5p0qml_IMG_3799.png" alt="" style={{height:'40px',opacity:0.7}} />
                </div>
              )}
            </div>
            <div className="sp-radio-info">
              <div className="sp-hero-tag"><span className="sp-live-dot" /> {isHoward ? 'HOWARD BURNS ON AIR' : (stationState?.segment_type === 'bumper') ? 'STATION ID' : track ? 'NOW PLAYING' : 'UP NEXT'}</div>
              <h2 className="sp-hero-title" style={{fontSize:'1rem'}}>{isHoward ? 'DJ BREAK' : track ? track.title : (nextTrack?.title || 'SPACE PIFF RADIO')}</h2>
              <div className="sp-hero-artist">{isHoward ? 'SpacePiff Radio' : track ? track.artist : (nextTrack?.artist || '')}</div>
              <div className="sp-hero-tags">
                {track?.genre && <span className="sp-tag">{track.genre}</span>}
                {track?.mood && <span className="sp-tag">{track.mood}</span>}
                {track?.is_new && <span className="sp-tag sp-tag-fresh"><Fire size={10} weight="fill" /> FRESH IN THE MOX</span>}
              </div>
              <div className="sp-hero-controls" style={{marginTop:'8px'}}>
                {!tunedIn ? (<button onClick={tuneIn} className="sp-btn" data-testid="tune-in-btn"><Radio size={16} weight="fill" /> [ TUNE IN ]</button>) :
                 (<button onClick={toggleMute} className="sp-btn-outline sp-nav-btn" data-testid="tune-out-btn">{isMuted ? <SpeakerSlash size={16}/> : <SpeakerHigh size={16}/>} {isMuted ? '[ UNMUTE ]' : 'LISTENING'}</button>)}
              </div>
              {track && nextTrack && <div className="sp-next-up" data-testid="next-up">NEXT UP: {nextTrack.title} — {nextTrack.artist}</div>}
            </div>
          </div>
        )}
      </section>

      {/* Fresh in the Mox — clickable tracks */}
      {stationState?.fresh_tracks?.length > 0 && (
        <section className="sp-section" data-testid="fresh-section">
          <div className="sp-section-label"><Fire size={16} weight="fill" /> FRESH IN THE MOX</div>
          <div className="sp-grid">
            {stationState.fresh_tracks.map(t => (
              <Link to={`/track/${t.id}`} key={t.id} className="sp-card" data-testid={`track-card-${t.id}`}>
                <img src={t.thumbnail} alt="" className="sp-card-thumb" />
                <div className="sp-card-info">
                  <div className="sp-card-title">{t.title}</div>
                  <div className="sp-card-artist">{t.artist}</div>
                  {t.is_new && <div className="sp-card-stats"><span style={{color:'var(--sp-green)'}}>FRESH</span></div>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Just Played — clickable */}
      {stationState?.history_tracks?.length > 0 && (
        <section className="sp-section" data-testid="history-section">
          <div className="sp-section-label"><Clock size={16} /> JUST PLAYED</div>
          <div className="sp-history-list">
            {stationState.history_tracks.map((t, i) => (
              <Link to={`/track/${t.id}`} key={t.id} className="sp-history-item" data-testid={`history-item-${i}`}>
                <img src={t.thumbnail} alt="" className="sp-history-thumb" />
                <div className="sp-history-text"><span className="sp-history-title">{t.title}</span><span className="sp-history-artist">{t.artist}</span></div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <footer className="sp-footer" data-testid="sp-footer">
        <div className="sp-footer-slate">
          <div className="sp-footer-line sp-footer-broadcast">
            Broadcasting from the Space Weed Universe.
          </div>
          <div className="sp-footer-line sp-footer-quiet">
            <a
              href="#transmission-list"
              className="sp-footer-anchor"
              data-testid="footer-signal-link"
              onClick={(e) => {
                e.preventDefault();
                const el = document.querySelector('[data-testid="transmission-section-home"]');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
            >
              Receive the Signal.
            </a>
          </div>
          <div className="sp-footer-line sp-footer-quiet">
            <a
              href="mailto:transmission@spacepiff.com"
              className="sp-footer-anchor"
              data-testid="footer-contact-link"
            >
              Transmission Contact.
            </a>
          </div>
          <div className="sp-footer-line sp-footer-mark">spacepiff.com</div>
        </div>
      </footer>
    </div>
  );
}

// ============ Track Detail Page ============
function TrackDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [track, setTrack] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [comments, setComments] = useState([]);
  const [likeEmail, setLikeEmail] = useState('');
  const [commentForm, setCommentForm] = useState({ email: '', display_name: '', text: '' });
  const [liked, setLiked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [copied, setCopied] = useState(false);

  const shareUrl = track?.slug ? `${window.location.origin}/api/t/${track.slug}` : `${window.location.origin}/api/tracks/${id}/og`;

  const handleShare = async () => {
    const shareData = {
      title: `${track?.title} — ${track?.artist}`,
      text: `Listen to ${track?.title} by ${track?.artist} on Space Piff Radio`,
      url: shareUrl
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch {}
    } else {
      try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
    }
  };

  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API}/tracks/${id}`); setTrack(r.data); } catch { setTrack(null); setNotFound(true); }
      try { setTracks((await axios.get(`${API}/tracks`)).data); } catch {}
    })();
  }, [id]);

  // Load comments if track has marsspace_slug
  useEffect(() => {
    if (!track?.marsspace_slug) return;
    (async () => {
      try { const r = await axios.get(`${API}/interactions/${track.marsspace_slug}/comments`); setComments(r.data.comments || []); } catch {}
    })();
  }, [track?.marsspace_slug]);

  const handleLike = async () => {
    if (!likeEmail || !track?.marsspace_slug) return;
    try { await axios.post(`${API}/interactions/public/like`, { track_slug: track.marsspace_slug, email: likeEmail }); setLiked(true); } catch {}
  };

  const handleComment = async () => {
    if (!commentForm.text || !commentForm.email || !track?.marsspace_slug) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/interactions/public/comment`, { track_slug: track.marsspace_slug, email: commentForm.email, display_name: commentForm.display_name || 'Anon Martian', text: commentForm.text });
      setCommentForm({ email: '', display_name: '', text: '' });
      const r = await axios.get(`${API}/interactions/${track.marsspace_slug}/comments`); setComments(r.data.comments || []);
    } catch {}
    setSubmitting(false);
  };

  const relatedTracks = tracks.filter(t => t.id !== id).slice(0, 4);

  if (!track && notFound) return (
    <div className="profile-page" data-testid="track-detail">
      <header className="profile-header-bar"><button onClick={() => navigate('/')} className="sp-nav-btn" data-testid="track-back"><ArrowLeft size={14} /> BACK TO RADIO</button></header>
      <div style={{textAlign:'center',padding:'80px 20px',color:'var(--sp-text-dim)'}}>
        <WifiSlash size={48} style={{marginBottom:'16px',opacity:0.5}} />
        <h2 style={{fontSize:'1rem',marginBottom:'8px'}}>TRANSMISSION NOT FOUND</h2>
        <p style={{fontSize:'0.7rem',marginBottom:'20px'}}>:: this track may have been removed ::</p>
        <button onClick={() => navigate('/')} className="sp-btn"><Radio size={14} /> [ BACK TO RADIO ]</button>
      </div>
    </div>
  );
  if (!track) return <div className="profile-page" data-testid="track-detail"><div style={{textAlign:'center',padding:'80px 0',color:'var(--sp-text-dim)'}}>:: loading transmission ::</div></div>;

  return (
    <div className="profile-page" data-testid="track-detail">
      <header className="profile-header-bar"><button onClick={() => navigate('/')} className="sp-nav-btn" data-testid="track-back"><ArrowLeft size={14} /> BACK TO RADIO</button></header>

      {/* Track hero */}
      <div className="profile-hero" data-testid="track-hero">
        <img src={track.thumbnail} alt={track.title} className="profile-pic" style={{width:'200px',height:'auto',aspectRatio:'16/9'}} />
        <div className="profile-details">
          <h1 className="profile-name" style={{fontSize:'1.5rem'}}>{track.title}</h1>
          <p className="profile-bio" style={{margin:'4px 0 8px'}}>{track.artist}</p>
          <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'10px'}}>
            {track.genre && <span className="sp-tag">{track.genre}</span>}
            {track.mood && <span className="sp-tag">{track.mood}</span>}
            {track.is_new && <span className="sp-tag sp-tag-fresh"><Fire size={10} /> FRESH</span>}
            <span className="sp-tag">{Math.floor(track.duration/60)}:{(track.duration%60).toString().padStart(2,'0')}</span>
          </div>
          <div style={{marginBottom:'10px'}}>
            <button onClick={handleShare} className="sp-btn" data-testid="share-track-btn" style={{fontSize:'0.6rem'}}>
              {copied ? <><CopySimple size={14} /> [ LINK COPIED ]</> : <><ShareNetwork size={14} /> [ SHARE TRANSMISSION ]</>}
            </button>
          </div>
          {track.description && <p style={{fontSize:'0.7rem',color:'var(--sp-text-dim)',lineHeight:1.5,margin:'0 0 10px'}}>{track.description.slice(0,300)}</p>}

          {/* On-demand player */}
          {track.youtube_video_id && (
            <div style={{marginTop:'10px'}}>
              <iframe
                width="100%" height="200" style={{border:'1px solid var(--sp-border-dim)'}}
                src={`https://www.youtube.com/embed/${track.youtube_video_id}?modestbranding=1&rel=0`}
                title={track.title} allow="autoplay; encrypted-media" allowFullScreen
                data-testid="track-on-demand-player"
              />
            </div>
          )}
        </div>
      </div>

      {/* Like */}
      {track.marsspace_slug && (
        <section className="sp-section" data-testid="track-like-section">
          <div className="sp-section-label"><Heart size={16} weight="fill" /> SIGNAL BOOST</div>
          {liked ? (
            <div style={{color:'var(--sp-green)',fontSize:'0.75rem'}}>:: signal received :: thanks for the boost ::</div>
          ) : (
            <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap'}}>
              <input value={likeEmail} onChange={e => setLikeEmail(e.target.value)} placeholder=":: your email ::" className="sp-input" style={{maxWidth:'250px'}} data-testid="like-email" />
              <button onClick={handleLike} className="sp-btn" data-testid="like-btn" style={{opacity:likeEmail?1:0.4}}><Heart size={14} weight="fill" /> [ BOOST ]</button>
            </div>
          )}
        </section>
      )}

      {/* Comments */}
      {track.marsspace_slug && (
        <section className="sp-section" data-testid="track-comments-section">
          <div className="sp-section-label"><ChatCircle size={16} /> TRANSMISSIONS ({comments.length})</div>
          <div style={{display:'flex',flexDirection:'column',gap:'8px',marginBottom:'16px'}}>
            <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
              <input value={commentForm.email} onChange={e => setCommentForm({...commentForm, email: e.target.value})} placeholder=":: email ::" className="sp-input" style={{flex:1,minWidth:'150px'}} data-testid="comment-email" />
              <input value={commentForm.display_name} onChange={e => setCommentForm({...commentForm, display_name: e.target.value})} placeholder=":: name ::" className="sp-input" style={{flex:1,minWidth:'120px'}} data-testid="comment-name" />
            </div>
            <div style={{display:'flex',gap:'8px'}}>
              <input value={commentForm.text} onChange={e => setCommentForm({...commentForm, text: e.target.value})} placeholder=":: drop a transmission ::" className="sp-input" style={{flex:1}} data-testid="comment-text" onKeyDown={e => { if (e.key === 'Enter') handleComment(); }} />
              <button onClick={handleComment} disabled={submitting || !commentForm.text || !commentForm.email} className="sp-btn" data-testid="comment-submit" style={{opacity:(commentForm.text && commentForm.email)?1:0.4}}>[ SEND ]</button>
            </div>
          </div>
          {comments.map((c, i) => (
            <div key={i} style={{padding:'8px 10px',border:'1px solid var(--sp-border-dim)',background:'var(--sp-surface)',marginBottom:'2px'}}>
              <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'4px'}}>
                {c.avatar_url && <img src={c.avatar_url} alt="" style={{width:'20px',height:'20px',borderRadius:'50%'}} />}
                <span style={{fontSize:'0.65rem',fontWeight:700,color:'var(--sp-green)'}}>{c.martian_name || 'Anon'}</span>
                <span style={{fontSize:'0.5rem',color:'var(--sp-text-muted)'}}>{c.role || ''}</span>
              </div>
              <div style={{fontSize:'0.7rem',color:'var(--sp-text-dim)'}}>{c.comment_text}</div>
            </div>
          ))}
        </section>
      )}

      {/* Related tracks */}
      {relatedTracks.length > 0 && (
        <section className="sp-section" data-testid="related-tracks">
          <div className="sp-section-label"><MusicNote size={16} /> MORE TRANSMISSIONS</div>
          <div className="sp-grid">
            {relatedTracks.map(t => (
              <Link to={`/track/${t.id}`} key={t.id} className="sp-card">
                <img src={t.thumbnail} alt="" className="sp-card-thumb" />
                <div className="sp-card-info"><div className="sp-card-title">{t.title}</div><div className="sp-card-artist">{t.artist}</div></div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ============ Admin Auth Gate ============
function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/admin-login`, { email, pin });
      localStorage.setItem('sp_admin_token', r.data.token);
      onLogin(r.data.token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Access denied');
    }
    setLoading(false);
  };

  return (
    <div className="admin-page" data-testid="admin-login-page" style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:'80vh'}}>
      <div style={{border:'1px solid var(--sp-border-dim)',padding:'40px',maxWidth:'380px',width:'100%'}}>
        <div style={{textAlign:'center',marginBottom:'24px'}}>
          <Gear size={28} style={{color:'var(--sp-green)'}} />
          <h2 style={{fontSize:'1rem',fontWeight:800,marginTop:'8px',letterSpacing:'0.1em'}}>MISSION CONTROL</h2>
          <p style={{fontSize:'0.6rem',color:'var(--sp-text-dim)',marginTop:'4px'}}>:: authorized personnel only ::</p>
        </div>
        <form onSubmit={handleSubmit} style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <div className="field-group"><label className="field-label">EMAIL</label><input value={email} onChange={e => setEmail(e.target.value)} placeholder=":: admin email ::" className="sp-input" data-testid="admin-email" autoComplete="email" /></div>
          <div className="field-group"><label className="field-label">PIN</label><input type="password" value={pin} onChange={e => setPin(e.target.value)} placeholder=":: access pin ::" className="sp-input" data-testid="admin-pin" autoComplete="current-password" maxLength={10} /></div>
          {error && <div style={{color:'var(--sp-red)',fontSize:'0.65rem',textAlign:'center'}} data-testid="admin-login-error">:: {error} ::</div>}
          <button type="submit" disabled={loading || !email || !pin} className="sp-btn" data-testid="admin-login-btn" style={{width:'100%',justifyContent:'center',opacity:(email && pin)?1:0.4}}>
            {loading ? '[ AUTHENTICATING... ]' : '[ ACCESS MISSION CONTROL ]'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ============ Admin Intake ============
function AdminIntake() {
  const [adminToken, setAdminToken] = useState(localStorage.getItem('sp_admin_token') || '');
  const [authChecked, setAuthChecked] = useState(false);
  const [url, setUrl] = useState('');
  const [tracks, setTracks] = useState([]);
  const [intaking, setIntaking] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [editTrack, setEditTrack] = useState(null);
  const [form, setForm] = useState({ artist: '', title: '', genre: '', mood: '', project: '', summary_for_howard: '', is_new: true });
  const [analytics, setAnalytics] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectUrl, setProjectUrl] = useState('');
  const [projectForm, setProjectForm] = useState({ title: '', artist: '', description: '', genre: '', mood: '' });
  const [addingProject, setAddingProject] = useState(false);
  const { fetchStationState, setIsAdmin, adminSkip } = useStation();
  const navigate = useNavigate();

  const authHeaders = { headers: { Authorization: `Bearer ${adminToken}` } };

  // Verify stored token on mount
  useEffect(() => {
    if (!adminToken) { setAuthChecked(true); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/verify`, authHeaders);
        if (!r.data.authenticated) { localStorage.removeItem('sp_admin_token'); setAdminToken(''); }
      } catch { localStorage.removeItem('sp_admin_token'); setAdminToken(''); }
      setAuthChecked(true);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setIsAdmin(true); return () => setIsAdmin(false); }, [setIsAdmin]);
  const loadTracks = useCallback(async () => { try { setTracks((await axios.get(`${API}/tracks`)).data); } catch {} }, []);
  useEffect(() => { if (adminToken) loadTracks(); }, [loadTracks, adminToken]);
  useEffect(() => {
    if (!adminToken) return;
    (async () => { try { setProjects((await axios.get(`${API}/projects`, authHeaders)).data); } catch {} })();
  }, [adminToken]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!adminToken) return;
    const fetchAnalytics = async () => { try { setAnalytics((await axios.get(`${API}/station/analytics`, authHeaders)).data); } catch {} };
    fetchAnalytics();
    const i = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(i);
  }, [adminToken]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!authChecked) return <div className="admin-page"><div style={{textAlign:'center',padding:'80px 0',color:'var(--sp-text-dim)'}}>:: verifying access ::</div></div>;
  if (!adminToken) return <AdminLogin onLogin={(token) => setAdminToken(token)} />;

  const handleSync = async () => { setSyncing(true); setSyncResult(null); try { setSyncResult((await axios.post(`${API}/sync/marsspace`, {}, authHeaders)).data); loadTracks(); fetchStationState(); } catch(e) { setSyncResult({error: e.response?.data?.detail||'Sync failed'}); } setSyncing(false); };
  const handleIntake = async () => { if(!url.trim()) return; setIntaking(true); try { await axios.post(`${API}/tracks/intake`,{youtube_url:url,...form}, authHeaders); setUrl(''); setForm({artist:'',title:'',genre:'',mood:'',project:'',summary_for_howard:'',is_new:true}); loadTracks(); await axios.post(`${API}/station/initialize`, {}, authHeaders); fetchStationState(); } catch(e) { alert(e.response?.data?.detail||'Failed'); } setIntaking(false); };
  const handleDelete = async (id) => { if(!window.confirm('Delete?')) return; try { await axios.delete(`${API}/tracks/${id}`, authHeaders); loadTracks(); await axios.post(`${API}/station/initialize`, {}, authHeaders); fetchStationState(); } catch{} };
  const openEdit = (t) => setEditTrack({id:t.id,title:t.title||'',artist:t.artist||'',genre:t.genre||'',mood:t.mood||'',project:t.project||'',description:t.description||'',lyrics_excerpt:t.lyrics_excerpt||'',summary_for_howard:t.summary_for_howard||'',thumbnail:t.thumbnail||'',is_new:t.is_new??true});
  const handleUpdate = async () => { if(!editTrack) return; try { const{id,...data}=editTrack; await axios.put(`${API}/tracks/${id}`,data, authHeaders); setEditTrack(null); loadTracks(); fetchStationState(); } catch{alert('Failed');} };
  const handleLogout = () => { localStorage.removeItem('sp_admin_token'); setAdminToken(''); };

  const handleAddProject = async () => {
    if (!projectUrl.trim()) return;
    setAddingProject(true);
    try {
      await axios.post(`${API}/projects`, { youtube_url: projectUrl, ...projectForm }, authHeaders);
      setProjectUrl(''); setProjectForm({ title: '', artist: '', description: '', genre: '', mood: '' });
      setProjects((await axios.get(`${API}/projects`, authHeaders)).data);
    } catch(e) { alert(e.response?.data?.detail || 'Failed'); }
    setAddingProject(false);
  };
  const handleDeleteProject = async (id) => {
    if (!window.confirm('Delete project?')) return;
    try { await axios.delete(`${API}/projects/${id}`, authHeaders); setProjects(projects.filter(p => p.id !== id)); } catch {}
  };
  const handleToggleFeatured = async (id, current) => {
    try { await axios.put(`${API}/projects/${id}`, { is_featured: !current }, authHeaders); setProjects((await axios.get(`${API}/projects`, authHeaders)).data); } catch {}
  };

  return (
    <div className="admin-page" data-testid="admin-page">
      <header className="admin-header">
        <button onClick={() => navigate('/')} className="sp-nav-btn" data-testid="admin-back"><ArrowLeft size={14} /> BACK</button>
        <h1 className="admin-title"><Gear size={20} /> MISSION CONTROL</h1>
        <div style={{display:'flex',gap:'8px'}}>
          <button onClick={() => { axios.post(`${API}/station/advance`, {}, authHeaders).then(() => { fetchStationState(); }); }} className="sp-nav-btn sp-nav-btn-red admin-skip-btn" data-testid="admin-skip"><SkipForward size={14} weight="fill" /> SKIP</button>
          <button onClick={handleLogout} className="sp-nav-btn" data-testid="admin-logout" style={{fontSize:'0.55rem'}}>[ LOGOUT ]</button>
        </div>
      </header>

      {/* Listener Analytics */}
      {analytics && (
        <section className="intake-section" data-testid="analytics-section">
          <div className="sp-section-label"><Broadcast size={16} /> LISTENER INTEL</div>
          <div className="sp-analytics-grid">
            <div className="sp-stat-card" data-testid="live-listeners">
              <div className="sp-stat-value"><span className="sp-live-dot" /> {analytics.live_now}</div>
              <div className="sp-stat-label">LIVE NOW</div>
            </div>
            <div className="sp-stat-card" data-testid="today-listeners">
              <div className="sp-stat-value">{analytics.daily?.[0]?.unique_listeners || 0}</div>
              <div className="sp-stat-label">TODAY</div>
            </div>
            <div className="sp-stat-card" data-testid="total-listeners">
              <div className="sp-stat-value">{analytics.total_unique_all_time}</div>
              <div className="sp-stat-label">ALL TIME</div>
            </div>
          </div>
          {analytics.daily?.length > 1 && (
            <div className="sp-daily-log" data-testid="daily-log">
              {analytics.daily.slice(0, 14).map(d => (
                <div key={d.date} className="sp-daily-row">
                  <span className="sp-daily-date">{d.date}</span>
                  <div className="sp-daily-bar" style={{width: `${Math.min(100, (d.unique_listeners / Math.max(...analytics.daily.map(x => x.unique_listeners))) * 100)}%`}} />
                  <span className="sp-daily-count">{d.unique_listeners}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Featured Projects Management */}
      <section className="intake-section" data-testid="projects-section">
        <div className="sp-section-label"><Fire size={16} weight="fill" /> FEATURED PROJECTS</div>
        <div className="intake-form">
          <div className="intake-url-row">
            <input type="text" value={projectUrl} onChange={e=>setProjectUrl(e.target.value)} placeholder=":: paste youtube url for project ::" className="sp-input" data-testid="project-url-input" />
            <button onClick={handleAddProject} disabled={addingProject||!projectUrl.trim()} className="sp-btn" data-testid="project-submit" style={{opacity:addingProject||!projectUrl.trim()?0.4:1}}>{addingProject?'[ PROCESSING... ]':'[ ADD PROJECT ]'}</button>
          </div>
          <div className="intake-fields">
            {[['TITLE','title','Project title...'],['ARTIST','artist','Artist name...'],['GENRE','genre','e.g. Sci-Fi Hip Hop'],['MOOD','mood','e.g. epic']].map(([l,k,p]) => (
              <div className="field-group" key={k}><label className="field-label">{l}</label><input value={projectForm[k]} onChange={e=>setProjectForm({...projectForm,[k]:e.target.value})} placeholder={p} className="sp-input" data-testid={`project-${k}`} /></div>
            ))}
            <div className="field-group full-width"><label className="field-label">DESCRIPTION</label><input value={projectForm.description} onChange={e=>setProjectForm({...projectForm,description:e.target.value})} placeholder="Short description..." className="sp-input" data-testid="project-description" /></div>
          </div>
        </div>
        {projects.length > 0 && (
          <div style={{marginTop:'12px'}}>
            {projects.map(p => (
              <div key={p.id} className="library-track" style={{display:'flex',alignItems:'center',gap:'12px',padding:'10px',border:'1px solid var(--sp-border-dim)',marginBottom:'4px'}}>
                {p.thumbnail && <img src={p.thumbnail} alt="" style={{width:'60px',height:'34px',objectFit:'cover',border:'1px solid var(--sp-border-dim)'}} />}
                <div style={{flex:1}}>
                  <div style={{fontSize:'0.7rem',fontWeight:700,color:'var(--sp-green)'}}>{p.title}</div>
                  <div style={{fontSize:'0.55rem',color:'var(--sp-text-dim)'}}>{p.artist}</div>
                </div>
                <button onClick={() => handleToggleFeatured(p.id, p.is_featured)} className="sp-btn" style={{fontSize:'0.5rem',padding:'4px 10px',background:p.is_featured?'rgba(57,255,20,0.15)':'transparent'}} data-testid={`project-feature-${p.id}`}>
                  {p.is_featured ? '[ FEATURED ]' : '[ SET FEATURED ]'}
                </button>
                <button onClick={() => handleDeleteProject(p.id)} className="sp-btn" style={{fontSize:'0.5rem',padding:'4px 8px',color:'var(--sp-red)',borderColor:'var(--sp-red)'}} data-testid={`project-delete-${p.id}`}><Trash size={12} /></button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="intake-section" data-testid="sync-section">
        <div className="sp-section-label"><Globe size={16} /> SYNC FROM THEMARSSPACE</div>
        <div className="intake-form" style={{display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap'}}>
          <button onClick={handleSync} disabled={syncing} className="sp-btn" data-testid="sync-btn" style={{opacity:syncing?0.5:1}}><ArrowsClockwise size={14} className={syncing?'sp-spin':''} /> {syncing?'[ SYNC TRACKS ]':'[ SYNC TRACKS ]'}</button>
          <button onClick={async () => { try { const r = await axios.post(`${API}/howard/generate-all`, {}, authHeaders); alert(`Howard generated for ${r.data.generated} tracks`); } catch(e) { alert(e.response?.data?.detail || 'Failed'); }}} className="sp-btn" data-testid="generate-all-howard" style={{borderColor:'var(--sp-green)'}}>
            <Microphone size={14} /> [ GENERATE ALL HOWARD ]
          </button>
          <span style={{fontSize:'0.6rem',color:'var(--sp-text-dim)'}}>:: pull from themarsspace.com + generate AI DJ ::</span>
          {syncResult && !syncResult.error && <span style={{fontSize:'0.6rem',color:'var(--sp-green)'}}>synced: {syncResult.synced} | updated: {syncResult.skipped}</span>}
          {syncResult?.error && <span style={{fontSize:'0.6rem',color:'var(--sp-red)'}}>{syncResult.error}</span>}
        </div>
      </section>

      <section className="intake-section" data-testid="intake-section">
        <div className="sp-section-label"><Upload size={16} /> UPLOAD TRANSMISSION</div>
        <div className="intake-form">
          <div className="intake-url-row">
            <input type="text" value={url} onChange={e=>setUrl(e.target.value)} placeholder=":: paste youtube url ::" className="sp-input" data-testid="intake-url-input" />
            <button onClick={handleIntake} disabled={intaking||!url.trim()} className="sp-btn" data-testid="intake-submit" style={{opacity:intaking||!url.trim()?0.4:1}}>{intaking?'[ PROCESSING... ]':'[ FETCH & ADD ]'}</button>
          </div>
          <div className="intake-fields">
            {[['ARTIST','artist','Override artist...'],['TITLE','title','Override title...'],['GENRE','genre','e.g. Sci-Fi Hip Hop'],['MOOD','mood','e.g. epic revolutionary'],['PROJECT','project','Album or project...']].map(([l,k,p]) => (
              <div className="field-group" key={k}><label className="field-label">{l}</label><input value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})} placeholder={p} className="sp-input" data-testid={`intake-${k}`} /></div>
            ))}
            <div className="field-group full-width"><label className="field-label">HOWARD'S NOTES</label><input value={form.summary_for_howard} onChange={e=>setForm({...form,summary_for_howard:e.target.value})} placeholder="What should Howard know?" className="sp-input" data-testid="intake-howard-notes" /></div>
            <div className="field-group"><label className="checkbox-label"><input type="checkbox" checked={form.is_new} onChange={e=>setForm({...form,is_new:e.target.checked})} data-testid="intake-is-new" /> FRESH IN THE MOX</label></div>
          </div>
        </div>
      </section>

      <section className="library-section" data-testid="library-section">
        <div className="sp-section-label"><MusicNote size={16} /> TRANSMISSIONS ({tracks.length})</div>
        {tracks.length===0 ? <p style={{textAlign:'center',padding:'40px 0',color:'var(--sp-text-dim)',fontSize:'0.8rem'}}>:: no transmissions ::</p> : (
          <div className="library-list">
            {tracks.map(t => (
              <div key={t.id} className="library-item" data-testid={`library-item-${t.id}`}>
                <Link to={`/track/${t.id}`}><img src={t.thumbnail} alt="" className="library-thumb" /></Link>
                <div className="library-info">
                  <Link to={`/track/${t.id}`} style={{textDecoration:'none',color:'inherit'}}><span className="library-title">{t.title}</span></Link>
                  <span className="library-artist">{t.artist}</span>
                  <div className="library-meta">
                    {t.genre && <span className="lib-tag">{t.genre}</span>}
                    {t.mood && <span className="lib-tag">{t.mood}</span>}
                    {t.is_new && <span className="lib-tag lib-tag-fresh"><Fire size={8}/> NEW</span>}
                    {t.marsspace_slug && <span className="lib-tag lib-tag-synced"><Globe size={8}/> MARS</span>}
                    <span className="lib-tag">{Math.floor(t.duration/60)}:{(t.duration%60).toString().padStart(2,'0')}</span>
                    <span className="lib-tag">{t.play_count} plays</span>
                  </div>
                </div>
                <div className="library-actions">
                  <button onClick={()=>openEdit(t)} className="sp-nav-btn" style={{fontSize:'0.55rem',padding:'4px 10px'}} data-testid={`edit-track-${t.id}`}><PencilSimple size={12}/> EDIT</button>
                  <button onClick={()=>handleDelete(t.id)} className="sp-nav-btn sp-nav-btn-red" style={{fontSize:'0.55rem',padding:'4px 10px'}} data-testid={`delete-track-${t.id}`}><Trash size={12}/></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {editTrack && (
        <div className="modal-overlay" data-testid="edit-track-modal">
          <div className="modal-content modal-wide">
            <div className="modal-header"><h2><PencilSimple size={14}/> EDIT TRANSMISSION</h2><button onClick={()=>setEditTrack(null)} className="modal-close" data-testid="close-edit-track"><X size={18}/></button></div>
            <div className="modal-body">
              {editTrack.thumbnail && <div className="edit-track-preview"><img src={editTrack.thumbnail} alt="" className="edit-track-thumb" /></div>}
              <div className="edit-track-grid">
                {[['TITLE','title'],['ARTIST','artist'],['GENRE','genre','e.g. Sci-Fi Hip Hop'],['MOOD','mood','e.g. epic'],['PROJECT','project'],['THUMBNAIL URL','thumbnail']].map(([l,k,p]) => (
                  <div className="field-group" key={k}><label className="field-label">{l}</label><input value={editTrack[k]||''} onChange={e=>setEditTrack({...editTrack,[k]:e.target.value})} placeholder={p||''} className="sp-input" data-testid={`edit-track-${k}`} /></div>
                ))}
                {[['DESCRIPTION','description'],['LYRICS EXCERPT','lyrics_excerpt','Key lyrics...'],["HOWARD'S NOTES",'summary_for_howard','What should Howard know?']].map(([l,k,p]) => (
                  <div className="field-group full-width" key={k}><label className="field-label">{l}</label><textarea value={editTrack[k]||''} onChange={e=>setEditTrack({...editTrack,[k]:e.target.value})} rows={2} placeholder={p||''} className="sp-textarea" data-testid={`edit-track-${k}`} /></div>
                ))}
                <div className="field-group"><label className="checkbox-label"><input type="checkbox" checked={editTrack.is_new} onChange={e=>setEditTrack({...editTrack,is_new:e.target.checked})} data-testid="edit-track-is-new" /> FRESH IN THE MOX</label></div>
              </div>
              <div className="modal-actions"><button onClick={()=>setEditTrack(null)} className="cancel-btn">CANCEL</button><button onClick={handleUpdate} className="sp-btn" data-testid="save-edit-track"><Check size={14}/> [ SAVE ]</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ Profile Page ============
function ProfilePage() {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [tracks, setTracks] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API}/profiles/${id}`); setProfile(r.data); setForm(r.data); } catch { setProfile(null); }
      try { setTracks((await axios.get(`${API}/tracks`)).data); } catch {}
    })();
  }, [id]);

  const handleSave = async () => { try { const r = await axios.put(`${API}/profiles/${id}`,{name:form.name,bio:form.bio,profile_pic:form.profile_pic,links:form.links}); setProfile(r.data); setEditing(false); } catch { alert('Failed'); } };

  if (!profile) return <div className="profile-page" data-testid="profile-page"><div style={{textAlign:'center',padding:'80px 0',color:'var(--sp-text-dim)'}}>:: loading ::</div></div>;

  return (
    <div className="profile-page" data-testid="profile-page">
      <header className="profile-header-bar"><button onClick={() => navigate('/')} className="sp-nav-btn" data-testid="profile-back"><ArrowLeft size={14} /> BACK TO RADIO</button></header>
      <div className="profile-hero" data-testid="profile-hero">
        <img src={profile.profile_pic||'https://via.placeholder.com/150'} alt={profile.name} className="profile-pic" />
        <div className="profile-details">
          <h1 className="profile-name">{profile.name}</h1>
          <p className="profile-bio">{profile.bio}</p>
          {profile.links?.website && <a href={profile.links.website} target="_blank" rel="noopener noreferrer" className="profile-link">{profile.links.website}</a>}
          <button onClick={()=>setEditing(true)} className="sp-nav-btn" data-testid="edit-profile-btn"><PencilSimple size={14}/> [ EDIT PROFILE ]</button>
        </div>
      </div>
      {editing && (
        <div className="modal-overlay" data-testid="edit-profile-modal">
          <div className="modal-content">
            <div className="modal-header"><h2>EDIT PROFILE</h2><button onClick={()=>setEditing(false)} className="modal-close" data-testid="close-edit-modal"><X size={18}/></button></div>
            <div className="modal-body">
              {[['NAME','name'],['BIO','bio'],['PROFILE PIC URL','profile_pic'],['WEBSITE','website']].map(([l,k]) => (
                <div className="field-group" key={k}><label className="field-label">{l}</label>
                  {k==='bio' ? <textarea value={form.bio||''} onChange={e=>setForm({...form,bio:e.target.value})} rows={3} className="sp-textarea" data-testid={`edit-${k}`}/> :
                   k==='website' ? <input value={form.links?.website||''} onChange={e=>setForm({...form,links:{...(form.links||{}),website:e.target.value}})} className="sp-input" data-testid={`edit-${k}`}/> :
                   <input value={form[k]||''} onChange={e=>setForm({...form,[k]:e.target.value})} className="sp-input" data-testid={`edit-${k}`}/>}
                </div>
              ))}
              {form.profile_pic && <img src={form.profile_pic} alt="Preview" className="edit-pic-preview" />}
              <button onClick={handleSave} className="sp-btn" data-testid="save-profile-btn"><Check size={14}/> [ SAVE PROFILE ]</button>
            </div>
          </div>
        </div>
      )}
      {tracks.length > 0 && (
        <section className="sp-section" data-testid="profile-tracks">
          <div className="sp-section-label"><MusicNote size={16} /> TRANSMISSIONS</div>
          <div className="sp-grid">{tracks.map(t => (<Link to={`/track/${t.id}`} key={t.id} className="sp-card"><img src={t.thumbnail} alt="" className="sp-card-thumb" /><div className="sp-card-info"><div className="sp-card-title">{t.title}</div><div className="sp-card-artist">{t.artist}</div></div></Link>))}</div>
        </section>
      )}
    </div>
  );
}

// ============ App ============
function NotFound() {
  return (
    <div className="sp-page" data-testid="not-found-page" style={{minHeight:'70vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'16px',textAlign:'center',padding:'40px 20px'}}>
      <div style={{fontFamily:'Unbounded, sans-serif',fontSize:'3rem',fontWeight:900,color:'var(--sp-green)'}}>404</div>
      <div style={{fontSize:'0.8rem',color:'var(--sp-text-dim)',letterSpacing:'0.1em'}}>:: SIGNAL LOST :: this frequency does not exist ::</div>
      <Link to="/" className="sp-btn" data-testid="not-found-home-link" style={{textDecoration:'none'}}>[ BACK TO THE BROADCAST ]</Link>
    </div>
  );
}

function AppContent() {
  return (
    <div className="app-shell">
      <Starfield />
      <Routes>
        <Route path="/" element={<RadioStation />} />
        <Route path="/admin" element={<AdminIntake />} />
        <Route path="/profile/:id" element={<ProfilePage />} />
        <Route path="/track/:id" element={<TrackDetail />} />
        <Route path="/arcade" element={<Suspense fallback={<div style={{background:'#000',minHeight:'100vh'}} />}><Game /></Suspense>} />
        <Route path="/go/marsspace" element={<GoMarsspace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <PlayerBar />
    </div>
  );
}

function App() {
  return (<BrowserRouter><StationProvider><AppContent /></StationProvider></BrowserRouter>);
}

export default App;
